import { useEffect, useRef, useState } from 'react'
import {
  ExternalLink,
  MessageSquare,
  RotateCcw,
  Tag,
  X,
} from 'lucide-react'
import { useIssueMessages, useCreateIssueMessage } from '../../hooks/useSystemIssueMessages'
import {
  useMarkIssueRead,
  useUpdateIssueStatus,
} from '../../hooks/useSystemIssues'
import {
  useMentionCandidatesForIssue,
  useTagMentions,
  useUntagMention,
} from '../../hooks/useSystemIssueMentions'
import { Avatar } from '../../../occurrences/components/common/Avatar'
import { TagColleaguesPicker } from '../shared/TagColleaguesPicker'
import { STATUS_DOT_CLASS, STATUS_LABELS } from '../shared/IssueRow'
import type {
  SystemIssue,
  SystemIssueMentionCandidate,
} from '../../types/system-issues'
import { useAuth } from '@/contexts/AuthContext'
import { BackButton } from '@/components/ui/BackButton'
import { IssueMessageItem } from './IssueMessageItem'
import { IssueMessageEditor } from './IssueMessageEditor'
import { IssuePropertiesPanel } from './IssuePropertiesPanel'

interface IssueThreadProps {
  issue: SystemIssue
  onBack: () => void
  /** Controles de status/atribuição só aparecem pra quem pode triagem (dev). */
  canManage: boolean
}

const processLinkClass =
  'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-blue-200 bg-[#eff6ff] px-2 py-0.5 text-[10px] font-medium text-[#1d55d6] transition hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/40'

/**
 * Versão simplificada de `SubjectThread` de Ocorrências: sem edição de
 * título, sem reply/citação — em compensação, ganha os controles de
 * status/atribuição (dev-only) que Ocorrências de processo não tem.
 */
export function IssueThread({ issue, onBack, canManage }: IssueThreadProps) {
  const { user } = useAuth()
  const { data: messages = [], isLoading } = useIssueMessages(issue.id)
  const createMessage = useCreateIssueMessage(issue.id)
  const updateStatus = useUpdateIssueStatus(issue.id)
  const markRead = useMarkIssueRead()
  const [showTagPicker, setShowTagPicker] = useState(false)
  const { data: mentionCandidates = [], isLoading: loadingCandidates } =
    useMentionCandidatesForIssue(showTagPicker ? issue.id : null)
  const tagMentions = useTagMentions(issue.id)
  const untagMention = useUntagMention(issue.id)
  const mentioned = issue.mentioned ?? []
  const mentionedIds = mentioned.map((m) => m.id)
  const isOwner = issue.created_by_id === user?.id
  const canRemoveMention = (accountId: number) =>
    isOwner || canManage || accountId === user?.id
  const handleToggleMention = (candidate: SystemIssueMentionCandidate) => {
    if (mentionedIds.includes(candidate.id)) {
      untagMention.mutate(candidate.id)
    } else {
      tagMentions.mutate([candidate.id])
    }
  }

  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const lastIssueIdRef = useRef<number | null>(null)
  const pendingScrollRef = useRef(false)
  useEffect(() => {
    if (lastIssueIdRef.current !== issue.id) {
      lastIssueIdRef.current = issue.id
      pendingScrollRef.current = true
      // Abrir a thread já conta como "li" — zera o badge de não lidas dela
      // (na lista do reporter e/ou na fila de triagem, dependendo de quem
      // está olhando).
      markRead.mutate(issue.id)
    }
  }, [issue.id])
  useEffect(() => {
    if (!pendingScrollRef.current) return
    if (isLoading) return
    const container = messagesContainerRef.current
    if (!container) return
    container.scrollTop = container.scrollHeight
    pendingScrollRef.current = false
  }, [messages, isLoading])

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-b border-border-subtle bg-surface px-4 py-3">
          <div className="flex items-center justify-between">
            <BackButton onClick={onBack} />
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-body">{issue.title}</h2>
              {issue.related_process && (
                <>
                  <a
                    href={`/processo/${issue.related_process.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={processLinkClass}
                  >
                    <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden />
                    Página do processo
                  </a>
                  <a
                    href={`/occurrences/${issue.related_process.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={processLinkClass}
                  >
                    <MessageSquare className="h-2.5 w-2.5 shrink-0" aria-hidden />
                    Ocorrências do processo
                  </a>
                </>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span className="inline-flex items-center gap-1.5 text-sm text-body/80 font-bold">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[issue.status]}`}
                  aria-hidden
                />
                {STATUS_LABELS[issue.status]}
              </span>
              {canManage && issue.status === 'resolved' && (
                <button
                  type="button"
                  onClick={() => updateStatus.mutate('open')}
                  disabled={updateStatus.isLoading}
                  className="inline-flex items-center gap-1.5 rounded-[5px] bg-gray-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-gray-600 disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reabrir
                </button>
              )}
            </div>
          </div>
          <div className="mt-1 text-[11px] text-muted">
            Reportado por <strong>{issue.created_by?.full_name ?? '—'}</strong>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {mentioned.map((m) => (
              <span
                key={m.id}
                className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-muted py-0.5 pl-1 pr-2 text-[11px] text-body/80"
              >
                <Avatar name={m.full_name} picture={m.picture} size={16} />
                {m.full_name}
                {canRemoveMention(m.id) && (
                  <button
                    type="button"
                    onClick={() => untagMention.mutate(m.id)}
                    disabled={untagMention.isLoading}
                    aria-label={`Remover marcação de ${m.full_name}`}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-surface disabled:opacity-50"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </span>
            ))}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTagPicker((v) => !v)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-border-subtle px-2 py-0.5 text-[11px] text-subtle transition hover:border-blue-400 hover:text-[#2563eb] dark:hover:text-blue-400"
              >
                <Tag className="h-3 w-3" />
                Marcar colega
              </button>
              {showTagPicker && (
                <div className="absolute left-0 top-full z-20 mt-1 w-64">
                  <TagColleaguesPicker
                    candidates={mentionCandidates}
                    selectedIds={mentionedIds}
                    onToggle={handleToggleMention}
                    loading={loadingCandidates}
                    disabled={tagMentions.isLoading || untagMention.isLoading}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          ref={messagesContainerRef}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3"
        >
          {isLoading && (
            <div className="text-center text-xs text-subtle">
              Carregando mensagens…
            </div>
          )}
          {messages.map((m) => (
            <IssueMessageItem
              key={m.id}
              message={m}
              isOwn={m.author_id === user?.id}
            />
          ))}
        </div>

        <IssueMessageEditor
          isSending={createMessage.isLoading}
          onSend={(content, attachments) => {
            createMessage.mutate(
              { content, attachments },
              {
                onSuccess: () => {
                  pendingScrollRef.current = true
                },
              },
            )
          }}
        />
      </div>
      <IssuePropertiesPanel issue={issue} canManage={canManage} />
    </div>
  )
}
